(() => {
  const root = document.querySelector('[data-ce-lp]');
  if (!root) return;
  document.documentElement.classList.add('ce-js');

  const tpl = document.querySelector('template[data-ce-offer-tpl]');
  root.querySelectorAll('[data-ce-offer]').forEach((slot) => {
    if (!tpl) return;
    slot.replaceChildren(tpl.content.cloneNode(true));
  });

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const reveal = root.querySelectorAll('[data-reveal]');
  if ('IntersectionObserver' in window && !reduceMotion) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    reveal.forEach((el) => io.observe(el));
  } else {
    reveal.forEach((el) => el.classList.add('is-in'));
  }

  root.querySelectorAll('video[data-ce-autoplay]').forEach((video) => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !reduceMotion) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(video);
  });

  const sticky = document.querySelector('[data-ce-sticky]');
  if (sticky && !root.querySelector('[data-ce-no-sticky]')) {
    const hero = root.querySelector('[data-ce-hero]');
    const offers = [...root.querySelectorAll('[data-ce-offer]')];
    let heroGone = !hero;
    let offerSeen = false;
    const link = sticky.querySelector('a');
    const sync = () => {
      const on = heroGone && !offerSeen;
      sticky.classList.toggle('is-visible', on);
      sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (link) link.tabIndex = on ? 0 : -1;
    };
    if ('IntersectionObserver' in window) {
      if (hero) {
        new IntersectionObserver(([e]) => {
          heroGone = !e.isIntersecting;
          sync();
        }).observe(hero);
      }
      const seen = new Set();
      const offerIo = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? seen.add(e.target) : seen.delete(e.target)));
        offerSeen = seen.size > 0;
        sync();
      });
      offers.forEach((o) => offerIo.observe(o));
    }
  }

  root.querySelectorAll('[data-ce-quiz]').forEach((quiz) => {
    const steps = [...quiz.querySelectorAll('.ce-q')];
    const result = quiz.querySelector('.ce-quiz__result');
    const moons = [...quiz.querySelectorAll('.ce-moons span')];
    const back = quiz.querySelector('[data-ce-back]');
    const again = quiz.querySelector('[data-ce-again]');
    const counter = quiz.querySelector('[data-ce-count]');
    let index = 0;
    let advanceTimer;

    const show = (i) => {
      window.clearTimeout(advanceTimer);
      index = i;
      steps.forEach((s, n) => s.classList.toggle('is-active', n === i));
      result.classList.toggle('is-active', i === steps.length);
      moons.forEach((m, n) => {
        m.classList.toggle('is-done', n < i);
        m.classList.toggle('is-now', n === i);
      });
      if (back) back.disabled = i === 0 || i === steps.length;
      if (counter) counter.textContent = i < steps.length ? `Question ${i + 1} of ${steps.length}` : 'Your result';
      const focusTarget = i === steps.length ? result.querySelector('[data-ce-outcome].is-active h3') : steps[i].querySelector('legend');
      if (focusTarget && quiz.dataset.started) {
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
        quiz.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }
    };

    const answer = (key) => {
      const el = quiz.querySelector(`input[name="${key}"]:checked`);
      return el ? el.value : '';
    };

    const fmt = (n) => {
      const r = Math.round(n * 4) / 4;
      const whole = Math.floor(r);
      const frac = r - whole;
      const f = frac === 0.25 ? '¼' : frac === 0.5 ? '½' : frac === 0.75 ? '¾' : '';
      return `${whole}${f}`;
    };

    const compute = () => {
      const position = answer('position');
      const shoulder = answer('shoulder') || 'average';
      const mattress = answer('mattress') || 'medium';
      const issue = answer('issue');
      const shoulderBase = { narrow: 3.5, average: 4.25, broad: 5, unknown: 4.25 }[shoulder] ?? 4.25;
      const mattressAdj = { soft: -0.5, medium: 0, firm: 0.5 }[mattress] ?? 0;
      const side = shoulderBase + mattressAdj;
      const backLoft = 3.25 + ({ soft: -0.25, medium: 0, firm: 0.25 }[mattress] ?? 0) + (shoulder === 'broad' ? 0.25 : 0);

      let outcome = 'switch';
      let low = backLoft - 0.5;
      let high = side + 0.5;
      if (position === 'side') {
        low = side - 0.5;
        high = side + 0.5;
        outcome = side < 3.75 ? 'side-low' : side > 5.25 ? 'side-high' : 'side';
      } else if (position === 'back') {
        low = backLoft - 0.5;
        high = backLoft + 0.5;
        outcome = 'back';
      } else if (position === 'stomach') {
        low = 1.5;
        high = 2.5;
        outcome = 'stomach';
      } else {
        low = Math.min(backLoft, side) - 0.5;
        high = Math.max(backLoft, side) + 0.5;
        outcome = 'switch';
      }
      return { outcome, low, high, issue };
    };

    const render = () => {
      const { outcome, low, high, issue } = compute();
      result.querySelectorAll('[data-ce-outcome]').forEach((o) => {
        o.classList.toggle('is-active', o.dataset.ceOutcome === outcome);
      });
      result.querySelectorAll('[data-ce-issue]').forEach((o) => {
        o.classList.toggle('is-active', o.dataset.ceIssue === issue);
      });
      result.querySelectorAll('[data-ce-range]').forEach((el) => {
        el.textContent = `${fmt(low)}–${fmt(high)}″`;
      });
    };

    const advance = (event) => {
      if (!event.target.matches('.ce-q input')) return;
      const step = steps.indexOf(event.target.closest('.ce-q'));
      if (step !== index) return;
      quiz.dataset.started = '1';
      window.clearTimeout(advanceTimer);
      advanceTimer = window.setTimeout(() => {
        if (step === steps.length - 1) render();
        show(step + 1);
      }, reduceMotion ? 0 : 260);
    };
    quiz.addEventListener('change', advance);
    quiz.addEventListener('click', advance);
    if (back) back.addEventListener('click', () => show(Math.max(0, index - 1)));
    if (again) {
      again.addEventListener('click', () => {
        quiz.querySelectorAll('.ce-q input').forEach((input) => (input.checked = false));
        show(0);
      });
    }
    show(0);
  });
})();
